import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Chip, Dialog, DialogContent, Grid, IconButton, Link as MuiLink, Paper, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import SquareFootOutlinedIcon from '@mui/icons-material/SquareFootOutlined';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import { getAmenityLabel, amenityIcons } from '../amenities';
import { getCategoryLabel } from '../categories';
import { getGearLabel, gearIcons } from '../gears';
import MediaDisplay from './MediaDisplay';
import RoomBookingDialog from './RoomBookingDialog';
import VideoPlayer from './VideoPlayer';
import { NotificationSnackbar } from './common/NotificationSnackbar';
import type { SpaceSummary } from '../models/Space';
import type { RoomSummary } from '../models/Room';
import { apiClient } from '../publicApiClient';
import { isVideoUrl } from '../utils/mediaUtils';
import type { PublicRoomListContext, RoomSpaceContext } from '../utils/publicOrgRooms';
import { publicSpacePagePath, spaceDetailPagePath } from '../utils/publicSpaceResolution';
import { isRoomBookingAllowed, roomBookingBlockedHint } from '../utils/roomBookingAccess';
import { toUrlFriendly } from '../utils/urlUtils';
import {
  readRoomBookingResumeFromSearchParams,
  stripRoomBookingResumeSearchParams,
} from '../utils/bookingLoginResume';
import { estimateHourlyBookingValue, trackStartBooking } from '../utils/analytics';

export type ContentViewMode = 'cards' | 'list';

/** @deprecated Use ContentViewMode */
export type RoomViewMode = ContentViewMode;

export interface PublicSpaceRoomListProps {
  space: SpaceSummary | PublicRoomListContext;
  viewMode: ContentViewMode;
  roomSpaceByRoomId?: Record<string, RoomSpaceContext>;
}

function defaultBookingSlot(): { dateTime: Date; durationMinutes: number } {
  const now = new Date();
  const minutes = Math.ceil(now.getMinutes() / 30) * 30;
  now.setMinutes(minutes, 0, 0);
  return {
    dateTime: new Date(now.getTime() + 2 * 60 * 60 * 1000),
    durationMinutes: 120,
  };
}

function formatPricePerHour(amount: number, currency: string | null | undefined, language: string): string {
  return new Intl.NumberFormat(language, {
    style: 'currency',
    currency: currency?.toString() || 'PLN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

function sortedRoomCategories(room: RoomSummary): string[] {
  return [...(room.categories ?? [])].sort((a, b) => {
    if (a === room.primaryCategory) return -1;
    if (b === room.primaryCategory) return 1;
    return 0;
  });
}

function RoomMetaSection({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Stack direction="row" spacing={0.55} alignItems="center" sx={{ mb: 0.5 }}>
        {icon}
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
}

const PublicSpaceRoomList: React.FC<PublicSpaceRoomListProps> = ({ space, viewMode, roomSpaceByRoomId }) => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rooms, setRooms] = useState<RoomSummary[]>(space.rooms ?? []);
  const [roomSteps, setRoomSteps] = useState<Record<string, number>>(() =>
    Object.fromEntries((space.rooms ?? []).map((room) => [room.id, 0])),
  );
  const [selectedRoom, setSelectedRoom] = useState<RoomSummary | null>(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [initialDateTime, setInitialDateTime] = useState<Date | undefined>();
  const [initialDuration, setInitialDuration] = useState<number | undefined>();
  const [initialStepKey, setInitialStepKey] = useState<'whoIsComing' | null>(null);
  const [roomGallery, setRoomGallery] = useState<{ room: RoomSummary; index: number } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    const nextRooms = space.rooms ?? [];
    setRooms(nextRooms);
    setRoomSteps(Object.fromEntries(nextRooms.map((room) => [room.id, 0])));
  }, [space.id, space.rooms]);

  useEffect(() => {
    if (!space.rooms?.length || !localStorage.getItem('access_token')) {
      return;
    }

    let cancelled = false;

    void Promise.all(space.rooms.map((room) => apiClient.getRoomSummary(room.id)))
      .then((summaries) => {
        if (cancelled) return;
        setRooms(
          space.rooms.map((room, index) => ({
            ...room,
            bookingAccess: summaries[index]?.bookingAccess ?? room.bookingAccess,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setRooms(space.rooms);
      });

    return () => {
      cancelled = true;
    };
  }, [space.id, space.rooms]);

  useEffect(() => {
    const resume = readRoomBookingResumeFromSearchParams(searchParams);
    if (!resume.open || !resume.roomId) return;
    const room = rooms.find((entry) => entry.id === resume.roomId);
    if (!room || !isRoomBookingAllowed(room)) return;

    let dt: Date | undefined;
    if (resume.roomAvailableFromTime) {
      const parsed = new Date(resume.roomAvailableFromTime);
      if (!isNaN(parsed.getTime())) dt = parsed;
    }
    setSelectedRoom(room);
    setInitialDateTime(dt);
    setInitialDuration(resume.durationInMinutes ?? undefined);
    setInitialStepKey('whoIsComing');
    trackStartBooking({
      provider_type: 'space',
      provider_id: space.id,
      city: space.city,
      currency: String(room.currecyCode ?? space.currencyCode ?? 'PLN'),
      booking_value: estimateHourlyBookingValue(Number(room.pricePerH), resume.durationInMinutes ?? 60),
    });
    setBookingOpen(true);
    setSearchParams(stripRoomBookingResumeSearchParams(searchParams), { replace: true });
  }, [rooms, searchParams, setSearchParams]);

  const currencyCode = useMemo(
    () => space.currencyCode?.toString() ?? rooms[0]?.currecyCode?.toString(),
    [rooms, space.currencyCode],
  );

  const openBooking = (room: RoomSummary) => {
    if (!isRoomBookingAllowed(room)) return;
    const { dateTime, durationMinutes } = defaultBookingSlot();
    setSelectedRoom(room);
    setInitialDateTime(dateTime);
    setInitialDuration(durationMinutes);
    trackStartBooking({
      provider_type: 'space',
      provider_id: space.id,
      city: space.city,
      currency: currencyCode ?? 'PLN',
      booking_value: estimateHourlyBookingValue(Number(room.pricePerH), durationMinutes),
    });
    setBookingOpen(true);
  };

  const shiftRoomImage = (roomId: string, delta: number, maxSteps: number) => {
    setRoomSteps((prev) => {
      const current = prev[roomId] ?? 0;
      const next = Math.min(Math.max(current + delta, 0), maxSteps - 1);
      return { ...prev, [roomId]: next };
    });
  };

  const copyRoomLink = async (room: RoomSummary) => {
    const roomSpace = roomSpaceByRoomId?.[room.id] ?? {
      id: room.spaceId,
      city: space.city,
      name: space.name,
    };
    const roomUrl = `${window.location.origin}${spaceDetailPagePath(roomSpace.city, roomSpace.name)}/${toUrlFriendly(room.name)}`;
    await navigator.clipboard.writeText(roomUrl);
    setSnackbarMessage(t('common.linkCopied', 'Link copied to clipboard'));
    setSnackbarOpen(true);
    void apiClient.trackEvent('Room Shared', {
      spaceName: roomSpace.name,
      roomId: room.id,
      roomName: room.name,
    });
  };

  const renderRoomThumbnail = (
    room: RoomSummary,
    step: number,
    maxSteps: number,
    imageSrc: string,
    layout: ContentViewMode,
  ) => (
    <Stack spacing={0.75} alignItems="center" sx={{ flexShrink: 0, minWidth: layout === 'list' ? { xs: 112, sm: 168 } : { xs: 96, sm: 120 } }}>
      <Box
        sx={{
          position: 'relative',
          width: layout === 'list' ? { xs: 112, sm: 168 } : { xs: 96, sm: 120 },
          height: layout === 'list' ? { xs: 84, sm: 120 } : { xs: 96, sm: 120 },
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.900',
        }}
        onClick={() => setRoomGallery({ room, index: step })}
      >
        <MediaDisplay
          src={imageSrc}
          alt={room.name}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
        />
        {maxSteps > 1 ? (
          <>
            <IconButton
              size="small"
              aria-label={t('common.previous', 'Previous')}
              onClick={(event) => {
                event.stopPropagation();
                shiftRoomImage(room.id, -1, maxSteps);
              }}
              disabled={step === 0}
              sx={{
                position: 'absolute',
                left: 2,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.88)',
                width: 24,
                height: 24,
              }}
            >
              <KeyboardArrowLeft sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              aria-label={t('common.next', 'Next')}
              onClick={(event) => {
                event.stopPropagation();
                shiftRoomImage(room.id, 1, maxSteps);
              }}
              disabled={step >= maxSteps - 1}
              sx={{
                position: 'absolute',
                right: 2,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.88)',
                width: 24,
                height: 24,
              }}
            >
              <KeyboardArrowRight sx={{ fontSize: 18 }} />
            </IconButton>
          </>
        ) : null}
      </Box>

      {layout === 'cards' ? (
        <Stack spacing={0.35} sx={{ width: '100%' }}>
          {room.area && room.areaUnit ? (
            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
              <SquareFootOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {room.area} {room.areaUnit}
              </Typography>
            </Stack>
          ) : null}
          {room.imageUrls?.length ? (
            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
              <PhotoLibraryOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {t('room.photoCount', '{{count}} photos', { count: room.imageUrls.length })}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );

  const renderRoomContent = (
    room: RoomSummary,
    categories: string[],
    priceLabel: string,
    bookable: boolean,
    bookingBlockedHintText: string | null,
    layout: ContentViewMode,
  ) => (
    <Stack spacing={1.15} flex={1} minWidth={0} sx={{ height: '100%' }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        justifyContent="space-between"
        flexWrap={layout === 'list' ? 'nowrap' : 'wrap'}
      >
        <Box minWidth={0}>
          <Typography variant="h6" sx={{ lineHeight: 1.25 }}>
            {room.name}
          </Typography>
          {(() => {
            const roomSpace = roomSpaceByRoomId?.[room.id];
            const spaceId = roomSpace?.id ?? room.spaceId;
            if (!roomSpace?.name) return null;

            const spacePath = spaceId ? publicSpacePagePath(spaceId) : null;
            const spaceLink = spacePath ? (
              <MuiLink
                component={RouterLink}
                to={spacePath}
                variant="subtitle2"
                underline="always"
                onClick={(event) => event.stopPropagation()}
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  textDecoration: 'underline',
                  textUnderlineOffset: 2,
                  verticalAlign: 'baseline',
                }}
              />
            ) : (
              <Typography component="span" variant="subtitle2" color="text.secondary" fontWeight={600} />
            );

            return (
              <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                <MeetingRoomOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary" component="span">
                  <Trans
                    i18nKey="schoolPage.roomInSpace"
                    defaults="Room at <spaceLink>{{spaceName}}</spaceLink>"
                    values={{ spaceName: roomSpace.name }}
                    components={{ spaceLink }}
                  />
                </Typography>
              </Stack>
            );
          })()}
          {layout === 'list' && (room.area && room.areaUnit || room.imageUrls?.length) ? (
            <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
              {room.area && room.areaUnit ? (
                <Stack direction="row" spacing={0.4} alignItems="center">
                  <SquareFootOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {room.area} {room.areaUnit}
                  </Typography>
                </Stack>
              ) : null}
              {room.imageUrls?.length ? (
                <Stack direction="row" spacing={0.4} alignItems="center">
                  <PhotoLibraryOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {t('room.photoCount', '{{count}} photos', { count: room.imageUrls.length })}
                  </Typography>
                </Stack>
              ) : null}
            </Stack>
          ) : null}
        </Box>
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={priceLabel}
          sx={{ fontWeight: 700, flexShrink: 0 }}
        />
      </Stack>

      {categories.length > 0 ? (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          {categories.map((category) => (
            <Chip
              key={`${room.id}-${category}`}
              size="small"
              color={room.primaryCategory === category ? 'primary' : 'default'}
              variant={room.primaryCategory === category ? 'filled' : 'outlined'}
              label={getCategoryLabel(category)}
            />
          ))}
        </Stack>
      ) : null}

      {room.description?.trim() ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            lineHeight: 1.45,
            display: '-webkit-box',
            WebkitLineClamp: layout === 'list' ? 2 : 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            whiteSpace: 'pre-line',
          }}
        >
          {room.description.trim()}
        </Typography>
      ) : null}

      {room.gears?.length ? (
        <RoomMetaSection
          icon={<MusicNoteRoundedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />}
          label={t('space.gears', 'Gears')}
        >
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {room.gears.map((gear, index) => {
              const icon = gearIcons[gear.gearType] || <AddIcon fontSize="small" />;
              return (
                <Chip
                  key={`${room.id}-gear-${gear.gearType}-${index}`}
                  size="small"
                  icon={icon}
                  label={getGearLabel(gear.gearType)}
                />
              );
            })}
          </Stack>
        </RoomMetaSection>
      ) : null}

      {room.amenities?.length ? (
        <RoomMetaSection
          icon={<AddIcon sx={{ fontSize: 15, color: 'text.secondary' }} />}
          label={t('space.amenities', 'Amenities')}
        >
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {room.amenities.map((amenity, index) => {
              const key = amenity.amenityType;
              const label = getAmenityLabel(key) || amenity.details;
              const icon = amenityIcons[key] || <AddIcon fontSize="small" />;
              return (
                <Chip
                  key={`${room.id}-amenity-${key}-${index}`}
                  size="small"
                  icon={icon}
                  label={label}
                />
              );
            })}
          </Stack>
        </RoomMetaSection>
      ) : null}

      <Stack
        direction={layout === 'list' ? { xs: 'column', md: 'row' } : { xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ mt: 'auto', pt: 1, width: '100%' }}
      >
        {bookable ? (
          <Button
            variant="contained"
            fullWidth
            startIcon={<CalendarMonthOutlinedIcon />}
            onClick={() => openBooking(room)}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {t('schoolPage.bookRoom', 'Book room')}
          </Button>
        ) : (
          <Tooltip title={bookingBlockedHintText || ''}>
            <span style={{ flex: 1, display: 'flex' }}>
              <Button
                variant="contained"
                fullWidth
                disabled
                startIcon={<CalendarMonthOutlinedIcon />}
                sx={{ minWidth: 0 }}
              >
                {t('schoolPage.bookRoom', 'Book room')}
              </Button>
            </span>
          </Tooltip>
        )}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<ShareOutlinedIcon />}
          onClick={() => void copyRoomLink(room)}
          sx={{ flex: 1, minWidth: 0 }}
        >
          {t('common.share', 'Share')}
        </Button>
      </Stack>

      {!bookable && bookingBlockedHintText ? (
        <Typography variant="caption" color="error">
          {bookingBlockedHintText}
        </Typography>
      ) : null}
    </Stack>
  );

  if (rooms.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <MeetingRoomOutlinedIcon color="disabled" />
          <Typography variant="h6">{t('schoolPage.noRoomsTitle', 'No rooms yet')}</Typography>
        </Stack>
        <Typography color="text.secondary">
          {t('schoolPage.noRoomsBody', 'This space does not have public rooms available right now.')}
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Grid container spacing={1.5}>
          {rooms.map((room) => {
            const step = roomSteps[room.id] ?? 0;
            const images = room.imageUrls ?? [];
            const maxSteps = Math.max(images.length, 1);
            const imageSrc = images[step] || '/logo512.png';
            const bookable = isRoomBookingAllowed(room);
            const bookingBlockedHintText = !bookable
              ? roomBookingBlockedHint(t, room.bookingAccess?.bookingBlockedReason ?? null)
              : null;
            const categories = sortedRoomCategories(room);
            const priceLabel = t('room.pricePerHourShort', '{{price}} / h', {
              price: formatPricePerHour(room.pricePerH, currencyCode, i18n.language || 'en'),
            });

            return (
              <Grid item xs={12} md={viewMode === 'cards' ? 6 : 12} key={room.id} sx={{ display: 'flex' }}>
                <Card
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s',
                    ...(bookable
                      ? {
                          '&:hover': {
                            borderColor: 'primary.main',
                            boxShadow: (theme) => theme.shadows[3],
                          },
                        }
                      : {}),
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={{ xs: 1.5, sm: 2 }}
                    alignItems="stretch"
                    sx={{ flex: 1 }}
                  >
                    {renderRoomThumbnail(room, step, maxSteps, imageSrc, viewMode)}
                    {renderRoomContent(
                      room,
                      categories,
                      priceLabel,
                      bookable,
                      bookingBlockedHintText,
                      viewMode,
                    )}
                  </Stack>
                </Card>
              </Grid>
            );
          })}
        </Grid>

      <Dialog open={Boolean(roomGallery)} onClose={() => setRoomGallery(null)} maxWidth="lg" fullWidth>
        <DialogContent sx={{ p: 0, bgcolor: 'black', position: 'relative' }}>
          {roomGallery ? (
            <>
              {isVideoUrl(roomGallery.room.imageUrls[roomGallery.index]) ? (
                <VideoPlayer
                  src={roomGallery.room.imageUrls[roomGallery.index]}
                  alt={`${roomGallery.room.name} - Gallery view`}
                  sx={{ width: '100%', height: 'auto', maxHeight: '90vh' }}
                />
              ) : (
                <MediaDisplay
                  src={roomGallery.room.imageUrls[roomGallery.index]}
                  alt={`${roomGallery.room.name} - Gallery view`}
                  showPlayButton={false}
                  sx={{ width: '100%', height: 'auto', maxHeight: '90vh', objectFit: 'contain' }}
                />
              )}
              {roomGallery.room.imageUrls.length > 1 ? (
                <>
                  <IconButton
                    onClick={() =>
                      setRoomGallery((current) =>
                        current
                          ? {
                              room: current.room,
                              index:
                                (current.index - 1 + current.room.imageUrls.length) %
                                current.room.imageUrls.length,
                            }
                          : null,
                      )
                    }
                    aria-label={t('common.previous', 'Previous')}
                    sx={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255, 255, 255, 0.85)',
                    }}
                  >
                    <KeyboardArrowLeft />
                  </IconButton>
                  <IconButton
                    onClick={() =>
                      setRoomGallery((current) =>
                        current
                          ? {
                              room: current.room,
                              index: (current.index + 1) % current.room.imageUrls.length,
                            }
                          : null,
                      )
                    }
                    aria-label={t('common.next', 'Next')}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255, 255, 255, 0.85)',
                    }}
                  >
                    <KeyboardArrowRight />
                  </IconButton>
                </>
              ) : null}
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {selectedRoom ? (
        <RoomBookingDialog
          open={bookingOpen}
          onClose={() => {
            setBookingOpen(false);
            setSelectedRoom(null);
            setInitialStepKey(null);
          }}
          room={selectedRoom}
          spaceName={roomSpaceByRoomId?.[selectedRoom.id]?.name ?? space.name}
          initialDateTime={initialDateTime}
          initialDuration={initialDuration}
          initialStepKey={initialStepKey}
        />
      ) : null}

      <NotificationSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity="success"
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default PublicSpaceRoomList;
